import { THIS_LEASE_CURRENTLY_CAN_NOT_ACCEPTS_PAYMENTS, PAYMENT_FEATURE_IS_CURRENTLY_UNAVAILABLE } from '~/assets/strings';
import { Presenter } from '~/framework/Presenter';
import { currency } from '~/global-contexts/utils/number';
import { getLeaseStatusColor, getLeaseStatusText } from '~/global-contexts/utils/statusColor';
import { User } from '~/state/mainAppState';
import { Lease } from '~/types/Lease';
import { LeaseRenewalOffer } from '~/types/LeaseRenewals';
import { LeaseStatus } from '~/types/LeaseStatus';
import { PaymentOptions } from '~/types/PaymentOptions';
import { PresentableUnit } from '~/types/PresentableUnit';

export interface PresentablePaymentOptions {
  currentAmount: string;
  postedAmount: string;
  customAmount: string;
  isPartialPaymentAllowed: boolean;
  paymentNotAllowedReason: string;
  isPaymentAllowed: boolean;
  isAutoPaymentAllowed?: boolean;
  autoPaymentNotAllowedReason?: string;
  shouldShowPostedBalance: boolean;
  shouldShowCurrentBallance: boolean;
  minimumPaymentAmount?: string;
  canAcceptPayments: boolean;
  canPayWithAch: boolean;
  canPayWithCard: boolean;
  currentlyProcessing: string;
}

export interface PresentableCategorizedLeases {
  allLeases: PresentableLease[];
  deactivatedLeases: PresentableLease[];
}

export interface PresentableProperty {
  id: string;
  displayName: string;
  address: {
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

export interface PresentableLease {
  id: string;
  unit: PresentableUnit;
  image?: string;
  thumbnail?: string;
  monthToMonth?: boolean;
  startDate?: string;
  endDate?: string;
  rent?: string;
  paymentOptions?: PresentablePaymentOptions;
  depositsPaymentOptions?: PresentablePaymentOptions;
  status: string;
  rawStatus?: LeaseStatus;
  statusColor: string;
  property?: PresentableProperty;
  depositPaid?: string;
  depositRequested?: string;
  currentBalance: string;
  postedBalance: string;
  offers?: LeaseRenewalOffer[];
}

export class LeasePresenter extends Presenter<PresentableCategorizedLeases> {
  protected createModel = (state: User): PresentableCategorizedLeases => {
    return {
      allLeases: state.leases?.map((lease) => createPresentableLease(lease)) ?? [],
      deactivatedLeases: state.deactivatedLeases.map((lease) => createPresentableLease(lease)),
    };
  };
}

export class SingleLeasePresenter extends Presenter<PresentableLease> {
  protected createModel = (state: User): PresentableLease | undefined => {
    const lease = state.selectedLease;
    if (lease) {
      return createPresentableLease(lease);
    }
    return undefined;
  };
}

const getAutoPaymentAllowed = (lease?: Lease): { isAutoPaymentEnabled: boolean; autoPaymentNotAllowedReason: string } => {
  const isPaymentAllowed = lease?.paymentOptions?.canAcceptPayments;
  const currentBalance = lease?.paymentOptions?.currentAmount || 0;

  if (!isPaymentAllowed) return { isAutoPaymentEnabled: false, autoPaymentNotAllowedReason: PAYMENT_FEATURE_IS_CURRENTLY_UNAVAILABLE };

  if (currentBalance < 0) {
    return {
      isAutoPaymentEnabled: false,
      autoPaymentNotAllowedReason: THIS_LEASE_CURRENTLY_CAN_NOT_ACCEPTS_PAYMENTS,
    };
  }

  return {
    isAutoPaymentEnabled: true,
    autoPaymentNotAllowedReason: '',
  };
};

const getPaymentNotAllowedReason = (paymentOptions?: PaymentOptions) => {
  const isPaymentAllowed = paymentOptions?.canAcceptPayments;
  const allowsPartialPayment = paymentOptions?.allowPartial;
  const currentBalance = paymentOptions?.currentAmount || 0;
  const postedBalance = paymentOptions?.postedAmount || 0;

  if (isPaymentAllowed) {
    if (!allowsPartialPayment && currentBalance <= 0 && postedBalance <= 0) {
      return { isAllowed: false, reason: THIS_LEASE_CURRENTLY_CAN_NOT_ACCEPTS_PAYMENTS };
    } else {
      return { isAllowed: true, reason: '' };
    }
  } else {
    return { isAllowed: false, reason: PAYMENT_FEATURE_IS_CURRENTLY_UNAVAILABLE };
  }
};

const getPaymentOptions = (lease?: Lease): PresentablePaymentOptions | undefined => {
  if (lease?.paymentOptions) {
    const minimumPaymentAmount = lease?.paymentOptions.minimumPaymentAmount;
    const customAmount = lease?.paymentOptions.customAmount ?? minimumPaymentAmount;
    const { isAllowed, reason } = getPaymentNotAllowedReason(lease.paymentOptions);
    const { isAutoPaymentEnabled: isAutoPaymentAllowed, autoPaymentNotAllowedReason } = getAutoPaymentAllowed(lease);
    return {
      currentAmount: currency(lease?.paymentOptions.currentAmount, { minimumFractionDigits: 2 }),
      postedAmount: currency(lease?.paymentOptions.postedAmount, { minimumFractionDigits: 2 }),
      customAmount: customAmount.toString(),
      isPartialPaymentAllowed: !!lease?.paymentOptions.allowPartial,
      isPaymentAllowed: isAllowed,
      paymentNotAllowedReason: reason,
      currentlyProcessing: currency(lease?.paymentOptions.currentlyProcessing, { minimumFractionDigits: 2 }),
      shouldShowCurrentBallance: lease?.paymentOptions.currentAmount > 0,
      shouldShowPostedBalance: lease?.paymentOptions.postedAmount > 0,
      minimumPaymentAmount: currency(minimumPaymentAmount, { minimumFractionDigits: 2 }),
      canAcceptPayments: lease?.paymentOptions.canAcceptPayments,
      canPayWithAch: lease?.paymentOptions.canPayWithAch,
      canPayWithCard: lease?.paymentOptions.canPayWithCard,
      isAutoPaymentAllowed,
      autoPaymentNotAllowedReason,
    };
  }
};

const getDepositsPaymentOptions = (lease?: Lease): PresentablePaymentOptions | undefined => {
  if (lease?.depositsPaymentOptions) {
    const minimumPaymentAmount = lease.depositsPaymentOptions.minimumPaymentAmount;
    const { isAllowed, reason } = getPaymentNotAllowedReason(lease.depositsPaymentOptions);

    return {
      currentAmount: currency(lease.depositsPaymentOptions.currentAmount, { minimumFractionDigits: 2 }),
      postedAmount: currency(lease.depositsPaymentOptions.postedAmount, { minimumFractionDigits: 2 }),
      customAmount: minimumPaymentAmount.toString(),
      isPartialPaymentAllowed: lease.depositsPaymentOptions.allowPartial ?? false,
      isPaymentAllowed: isAllowed,
      paymentNotAllowedReason: reason,
      shouldShowCurrentBallance: lease.depositsPaymentOptions.currentAmount > 0,
      shouldShowPostedBalance: lease.depositsPaymentOptions.postedAmount > 0,
      minimumPaymentAmount: currency(minimumPaymentAmount, { minimumFractionDigits: 2 }),
      currentlyProcessing: currency(lease.depositsPaymentOptions.currentlyProcessing, { minimumFractionDigits: 2 }),
      canAcceptPayments: lease?.depositsPaymentOptions.canAcceptPayments,
      canPayWithAch: lease.depositsPaymentOptions.canPayWithAch,
      canPayWithCard: lease.depositsPaymentOptions.canPayWithCard,
    };
  }
};

const createPresentableLease = (lease: Lease): PresentableLease => {
  return {
    id: lease.id,
    unit: {
      id: lease.unit?.id || '',
      name: lease.unit?.name || '',
      area: lease.unit?.unitSizeSqft?.toLocaleString() || '',
      bedrooms: lease.unit?.beds?.toString() || '',
      bathrooms: lease.unit?.baths?.toString() || '',
      address: lease.property?.address?.city + ', ' + lease.property?.address?.state || '',
      images: lease.unit.images,
    },
    image: lease.image,
    thumbnail: lease.thumbnail,
    monthToMonth: lease.monthToMonth,
    startDate: lease.start || '',
    endDate: lease.end || '',
    rent: currency(lease.currentRent),
    status: getLeaseStatusText(lease.status),
    statusColor: getLeaseStatusColor(lease.status),
    rawStatus: lease.status,
    property: {
      id: lease.property?.id || '',
      displayName: lease.property?.displayName || '',
      address: {
        streetAddress1: lease.property?.address?.streetAddress1 || '',
        streetAddress2: lease.property?.address?.streetAddress2 || '',
        city: lease.property?.address?.city || '',
        state: lease.property?.address?.state || '',
        zipCode: lease.property?.address?.zipCode || '',
        country: lease.property?.address?.country || '',
      },
    },
    paymentOptions: getPaymentOptions(lease),
    depositRequested: currency(lease.securityDepositRequested ?? 0),
    depositPaid: currency(lease.securityDepositPaid ?? 0),
    depositsPaymentOptions: getDepositsPaymentOptions(lease),
    currentBalance: currency(lease.currentBalance),
    postedBalance: currency(lease.postedBalance),
    offers: lease.offers,
  } as PresentableLease;
};
